import { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  Calendar, Dumbbell, Moon, RotateCcw, Settings, Sun, Target, TrendingUp, Utensils, X,
} from 'lucide-react';
import { deepEqual, merge3, serverPart, stateHash } from './fitnessSync';
import {
  THEME_KEY, defaultCycleAnchor, hasMeaningfulData, hydrateState, loadFitnessState, saveFitnessState,
  todayKey, workoutToday,
} from './fitness/state';
import { getSyncedAt, readSyncMeta, setSyncedAt, writePendingPut, writeSyncMeta } from './fitness/syncMeta';
import { FONT_BODY, UI_KEY, getTokens, readUi } from './fitness/theme';
import { planDayFor } from './fitness/logic/schedule';
import { TodayTab } from './fitness/TodayTab';
import { PlanTab } from './fitness/PlanTab';
import { HistoryTab } from './fitness/HistoryTab';
import { StatsTab } from './fitness/StatsTab';
import { ProfileTab } from './fitness/ProfileTab';
import { MealsTab } from './fitness/MealsTab';
import { RestPill } from './fitness/RestPill';
import { cancelRestPush, pushOn, registerServiceWorker, scheduleRestPush } from './fitness/push';
import { makeRestTimer, nextSetText, readRestTimer, restSecondsFor, withPlanRest, writeRestTimer } from './fitness/rest';

/* ────────────────────────────────────────────────────────────────────────── */
/*  Root component                                                            */
/* ────────────────────────────────────────────────────────────────────────── */

const TABS = [
  { id: 'today', label: 'Today', icon: Dumbbell },
  { id: 'plan', label: 'Plan', icon: Target },
  { id: 'meals', label: 'Meals', icon: Utensils },
  { id: 'history', label: 'History', icon: Calendar },
  { id: 'stats', label: 'Stats', icon: TrendingUp },
  { id: 'profile', label: 'Profile', icon: Settings },
];

export default function FitnessWorkspace() {
  const { isAuthenticated, isLoading, token, user } = useAuth();
  const [state, setState] = useState(loadFitnessState);
  const [tab, setTab] = useState('today');
  // The day Meals shows (null = today). Stats opens Meals on a past day with
  // it; tapping a tab resets it, like the old tab-local picker did.
  const [mealsDate, setMealsDate] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || 'dark');
  // UI template (Profile > Appearance), device-local like the theme.
  const [ui, setUi] = useState(readUi);
  const compact = ui === 'compact';
  // The running rest timer is device-local (docs/fitness-data-model.md): out
  // of the synced state, so +15/Skip never PUT and a pull never resets it.
  const [rest, setRest] = useState(() => readRestTimer());
  const skipServerSaveRef = useRef(false);
  const saveTimerRef = useRef(null);
  const initialLoadedRef = useRef(false);
  const stateRef = useRef(state); // mirrors `state` so hide/unload handlers read the latest value without re-binding

  const isDark = theme === 'dark';
  const t = getTokens(isDark, ui);

  const syncMetaRef = useRef(null);
  if (syncMetaRef.current === null) syncMetaRef.current = readSyncMeta();
  const syncChainRef = useRef(Promise.resolve());
  const lastPullRef = useRef(0);
  const [syncNotice, setSyncNotice] = useState(null);

  useEffect(() => {
    if (!syncNotice) return undefined;
    const handle = window.setTimeout(() => setSyncNotice(null), 6000);
    return () => window.clearTimeout(handle);
  }, [syncNotice]);

  // Server calls run one at a time, so a PUT always carries the version the
  // previous one returned and a refetch never races a save from this tab.
  const enqueueSync = (job) => {
    const next = syncChainRef.current.then(job, job);
    syncChainRef.current = next.catch(() => {});
    return next;
  };

  const markSynced = (version, base) => {
    const owner = user?.id != null ? String(user.id) : syncMetaRef.current.owner;
    syncMetaRef.current = { ...syncMetaRef.current, version, base, pending: null, owner };
    writeSyncMeta(version, base, owner);
    writePendingPut(null);
  };

  const scheduleSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { saveTimerRef.current = null; pushState(); }, 1500);
  };

  // Fold a server copy ({data, version, updated_at}) into local state.
  const integrateServer = (server) => {
    let meta = syncMetaRef.current;
    const prevVersion = meta.version;
    const local = serverPart(stateRef.current);
    const serverHasData = !!server.data && hasMeaningfulData(server.data);
    const serverState = server.data ? serverPart(hydrateState(server.data)) : null;
    const serverVersion = server.version || 0;

    // Local state belongs to another account: never merge or push it.
    if (meta.owner != null && user?.id != null && meta.owner !== String(user.id)) {
      skipServerSaveRef.current = true;
      setState(hydrateState(serverState || {}));
      markSynced(serverVersion, serverState || {});
      return;
    }
    // Server is behind what we last synced: the database was restored from a
    // backup or reset. Our base no longer describes it, so don't merge
    // against it; fall back to comparing timestamps below.
    if (meta.version != null && serverVersion < meta.version) {
      meta = { ...meta, version: null, base: null, pending: null };
    }
    let base = meta.base;

    // Our own PUT landed but its response was lost: that copy is our base.
    let ownWrite = false;
    if (serverState && meta.pending && serverVersion === meta.pending.baseVersion + 1
        && stateHash(serverState) === meta.pending.hash) {
      base = serverState;
      ownWrite = true;
    }

    let next;
    let conflicts = [];
    if (!serverState) {
      next = local; // nothing on the server yet
    } else if (base) {
      next = merge3(base, local, serverState, conflicts);
    } else {
      // No merge base: first sync since versioning shipped, or storage full.
      // Fall back to the old rule: adopt the server copy if it changed since
      // this device last synced, otherwise keep local and push it.
      const serverIsNewer = meta.version != null
        ? serverVersion !== meta.version
        : !!server.updated_at && (!getSyncedAt() || server.updated_at > getSyncedAt());
      next = serverHasData && serverIsNewer ? serverState : local;
      if (next === serverState && meta.version != null && !deepEqual(local, serverState)) conflicts = ['(no merge base)'];
    }

    markSynced(serverVersion, serverState || {});
    if (server.updated_at) setSyncedAt(server.updated_at);

    const needsPush = !serverState ? hasMeaningfulData(next) : !deepEqual(next, serverState);
    if (!deepEqual(next, local)) {
      if (!needsPush) skipServerSaveRef.current = true;
      setState(prev => hydrateState({ ...next, progressPhotos: prev.progressPhotos || [] }));
    } else if (needsPush) {
      scheduleSave();
    }
    if (conflicts.length) {
      setSyncNotice('Updated elsewhere — reloaded the latest data.');
    } else if (!ownWrite && prevVersion != null && serverVersion > prevVersion) {
      showCoachActivity(prevVersion);
    }
  };

  // Say what Claude changed since our last sync (the change journal), e.g.
  // "Claude: logged 82.4 kg". Silent when there is nothing or on any error.
  const showCoachActivity = (afterVersion) => {
    if (!token) return;
    fetch(`/api/fitness/changes?after_version=${afterVersion}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(res => {
        const lines = (res?.changes || []).filter(c => String(c.source || '').startsWith('mcp:') && c.summary)
          .map(c => c.summary);
        if (!lines.length) return;
        const text = lines.slice(-3).join(' · ');
        setSyncNotice(`Claude: ${text}${lines.length > 3 ? ` (+${lines.length - 3} more)` : ''}`);
      })
      .catch(() => {});
  };

  /* Restore through the API: save what's pending first so base_version is
   * current, then pull the result. A dry run only previews. */
  const restoreFitness = (body) => {
    if (!token) return Promise.resolve(null);
    if (!body.dry_run) flushPendingSave();
    return enqueueSync(() => fetch('/api/fitness/restore', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...body, base_version: syncMetaRef.current.version || 0 }),
    }).then(r => r.json().catch(() => null)).catch(() => null))
      .then(res => {
        if (!body.dry_run && res?.success) pullState();
        return res;
      });
  };

  // PUT the current state with the version it's based on. keepalive lets the
  // request outlive a backgrounded/closing tab (critical on iOS), but browsers
  // cap keepalive bodies at 64 KB, so larger states go as a normal request.
  const pushState = ({ keepalive = false } = {}) => enqueueSync(() => {
    if (!token) return null;
    const data = serverPart(stateRef.current);
    if (!hasMeaningfulData(data)) return null;
    const meta = syncMetaRef.current;
    if (meta.base && meta.version != null && deepEqual(data, meta.base)) return null; // nothing new
    const baseVersion = meta.version || 0;
    const body = JSON.stringify({ data, base_version: baseVersion });
    const pending = { baseVersion, hash: stateHash(data) };
    syncMetaRef.current = { ...meta, pending };
    writePendingPut(pending);
    return fetch('/api/fitness', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body,
      keepalive: keepalive && body.length < 60000,
    })
      .then(async r => {
        const res = await r.json().catch(() => null);
        if (r.ok && res?.success) {
          markSynced(res.version, data);
          if (res.updated_at) setSyncedAt(res.updated_at);
        } else if (r.status === 409 && res?.conflict) {
          integrateServer(res);
        }
      })
      .catch(() => {});
  });

  const pullState = () => enqueueSync(() => {
    if (!token) return null;
    lastPullRef.current = Date.now();
    return fetch('/api/fitness', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(res => { if (res && res.success) integrateServer(res); })
      .catch(() => {});
  });

  const flushPendingSave = () => {
    if (!saveTimerRef.current) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    pushState({ keepalive: true });
  };

  // Initial server sync on auth. integrateServer merges the server copy with
  // any local edits this device hadn't synced yet (e.g. a debounced save that
  // never ran because the app was killed right after a set), instead of one
  // side blindly overwriting the other.
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    enqueueSync(() => fetch('/api/fitness', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(res => {
        if (cancelled) return;
        if (res && res.success) integrateServer(res);
        lastPullRef.current = Date.now();
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) initialLoadedRef.current = true; }));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Persist. Always to localStorage (synchronous); to the server only after
  // the initial load ran and only when there's real data.
  useEffect(() => {
    saveFitnessState(state);
    stateRef.current = state;
    if (!token || !initialLoadedRef.current || skipServerSaveRef.current) {
      skipServerSaveRef.current = false;
      return undefined;
    }
    if (!hasMeaningfulData(state)) return undefined;
    scheduleSave();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, token]);

  useEffect(() => { writeRestTimer(rest); }, [rest]);

  // Lock-screen rest alerts: the server pushes when the rest ends. While the
  // app is still open at that moment it alerts itself, so the push is
  // cancelled just before; a locked phone runs no timers and gets the push.
  useEffect(() => { registerServiceWorker(); }, []);
  const restEnd = rest?.endsAt || 0;
  useEffect(() => {
    if (!token || !pushOn()) return undefined;
    const left = restEnd - Date.now();
    if (left <= 0) { cancelRestPush(token); return undefined; }
    const next = nextSetText(rest?.nextSet);
    scheduleRestPush(token, restEnd, next ? `Next: ${next}` : null);
    const id = window.setTimeout(() => {
      if (document.visibilityState === 'visible') cancelRestPush(token);
    }, Math.max(0, left - 3000));
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, restEnd]);

  // Flush any pending debounced save immediately when the app is backgrounded,
  // the tab loses visibility, or the page unloads — the scenarios where a
  // 1.5s debounce timer would otherwise never get to fire. When the app comes
  // back, pull the server copy so changes made elsewhere (another device,
  // Claude) show up without a reload.
  useEffect(() => {
    const pullIfStale = () => {
      if (!initialLoadedRef.current || Date.now() - lastPullRef.current < 3000) return;
      pullState();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushPendingSave();
      else pullIfStale();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flushPendingSave);
    window.addEventListener('focus', pullIfStale);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flushPendingSave);
      window.removeEventListener('focus', pullIfStale);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Reminders
  useEffect(() => {
    if (!state.reminders.enabled) return undefined;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return undefined;
    const id = window.setInterval(() => {
      const now = new Date();
      const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const k = todayKey();
      if (hhmm === state.profile.reminderTime && state.reminders.lastSentDate !== k) {
        const w = planDayFor(state, k, defaultCycleAnchor())?.day;
        new Notification(w?.isRest ? 'Rest day check-in' : `${w?.name || 'Workout'} reminder`, {
          body: w?.isRest ? 'Hydrate, eat, rest.' : `Time to train. ${w.name} is up.`,
        });
        setState(prev => ({ ...prev, reminders: { ...prev.reminders, lastSentDate: k } }));
      }
    }, 30000);
    return () => window.clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.plan, state.profile.reminderTime, state.reminders.enabled, state.reminders.lastSentDate, state.cycleAnchor, state.sessions]);

  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/" replace />;

  const startRest = (info) => setRest(makeRestTimer(info, Date.now()));
  const restPlanEx = rest?.planExId
    ? state.plan.flatMap(d => d.exercises || []).find(e => e.id === rest.planExId) || null
    : null;

  const ActiveTab = {
    today: <TodayTab t={t} state={state} setState={setState} isDark={isDark} restActive={!!rest} onRestStart={startRest} ui={ui} />,
    plan: <PlanTab t={t} state={state} setState={setState} isDark={isDark} />,
    meals: <MealsTab t={t} state={state} setState={setState} token={token} isDark={isDark}
      date={mealsDate} onDateChange={setMealsDate} />,
    history: <HistoryTab t={t} state={state} isDark={isDark} />,
    stats: <StatsTab t={t} state={state} setState={setState} isDark={isDark}
      onOpenMeals={(k) => { setMealsDate(k); setTab('meals'); window.scrollTo(0, 0); }} />,
    profile: <ProfileTab t={t} state={state} setState={setState} token={token} onRestore={restoreFitness}
      ui={ui} onUi={next => { setUi(next); try { localStorage.setItem(UI_KEY, next); } catch {} }}
      isDark={isDark} onTheme={next => { setTheme(next); localStorage.setItem(THEME_KEY, next); }}
      showTrading={!user?.fitness_only} />,
  }[tab];

  return (
    <div style={{
      minHeight: '100vh', background: t.bg, color: t.text, fontFamily: FONT_BODY,
      // Compact: Inter with tabular figures for numbers, and a tab bar that
      // fixed elements (rest timer, snackbars) sit above.
      ...(compact ? { '--fit-mono': '"Inter", -apple-system, sans-serif', fontVariantNumeric: 'tabular-nums',
        '--fit-bottom': 'calc(58px + env(safe-area-inset-bottom))' } : {}),
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600;700&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; }
        input, select, button { font-family: inherit; }
        input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
        input:focus, select:focus { border-color: ${t.accent} !important; }
        input::placeholder { color: ${t.textFaint}; opacity: 0.55; font-weight: 500; }
        input::-webkit-input-placeholder { color: ${t.textFaint}; opacity: 0.55; font-weight: 500; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-thumb { background: ${t.cardBorder}; border-radius: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        /* number inputs in grid cells must be allowed to shrink */
        input[type="number"] { min-width: 0; }

        /* ── Mobile responsive (≤ 600px) ───────────────────────────────── */
        @media (max-width: 600px) {
          /* Page-level breathing room */
          .fit-page { padding: 12px !important; gap: 12px !important; }

          /* Today header card (workout name + progress) */
          .fit-today-header { padding: 16px !important; }
          .fit-today-header-title { font-size: 22px !important; }
          .fit-today-header-pct { font-size: 18px !important; }

          /* Exercise card header (name + set count + chevron) */
          .fit-ex-head { padding: 12px 14px !important; gap: 8px !important; }
          .fit-ex-head-name { font-size: 14px !important; }
          .fit-ex-body { padding: 0 14px 12px !important; }

          /* Set tables — slimmer to keep all columns visible without scroll */
          .fit-set-grid {
            grid-template-columns: 26px minmax(0, 1fr) minmax(0, 1fr) 52px 26px !important;
            gap: 6px !important;
            padding: 5px 0 !important;
          }
          .fit-set-grid input {
            padding: 6px 4px !important; font-size: 13px !important;
          }
          .fit-set-grid-history {
            grid-template-columns: 26px minmax(0, 1fr) minmax(0, 1fr) 44px !important;
            gap: 6px !important;
            padding: 6px 0 !important;
          }

          /* 4-tile rows collapse to 2×2 (stats overview, profile macros) */
          .fit-quad { grid-template-columns: 1fr 1fr !important; }

          /* Plan tab — day editor header & exercise row stack vertically */
          .fit-plan-day-edit { grid-template-columns: 1fr !important; }
          .fit-plan-ex-head { grid-template-columns: 1fr 60px 60px 80px !important; gap: 6px !important; }
          .fit-plan-ex-row { grid-template-columns: 1fr 60px 60px 80px !important; gap: 6px !important; }
          .fit-plan-ex-row input { padding: 6px 6px !important; font-size: 13px !important; }

          /* "Today is" select — full width on mobile */
          .fit-today-select select { min-width: 0 !important; width: 100% !important; }
          .fit-today-select { width: 100%; }

          /* History day-cards stack their right-side stats more compactly */
          .fit-hist-card-head { padding: 12px 14px 10px !important; }
          .fit-hist-card-body { padding: 0 14px 12px !important; }

          /* Stats: progress chart toolbar stacks if needed */
          .fit-chart-toolbar { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }
          .fit-chart-toolbar select { width: 100% !important; }
        }
        @keyframes prToastIn {
          0%   { opacity: 0; transform: translate(-50%, -16px) scale(0.92); }
          55%  { opacity: 1; transform: translate(-50%, 4px) scale(1.04); }
          100% { opacity: 1; transform: translate(-50%, 0) scale(1); }
        }
        @keyframes prToastOut {
          to { opacity: 0; transform: translate(-50%, -10px) scale(0.96); }
        }
        @keyframes prTrophySpin {
          0%   { transform: rotate(-12deg) scale(1); }
          25%  { transform: rotate(14deg) scale(1.18); }
          50%  { transform: rotate(-10deg) scale(1.1); }
          75%  { transform: rotate(8deg) scale(1.14); }
          100% { transform: rotate(0) scale(1); }
        }
        @keyframes prGlow {
          0%, 100% { box-shadow: 0 12px 40px rgba(245, 158, 11, 0.35), 0 0 0 0 rgba(245, 158, 11, 0.5); }
          50%      { box-shadow: 0 16px 48px rgba(245, 158, 11, 0.55), 0 0 0 10px rgba(245, 158, 11, 0); }
        }
        @keyframes prSparkle {
          0%   { opacity: 0; transform: translate(0, 0) scale(0.4); }
          40%  { opacity: 1; }
          100% { opacity: 0; transform: var(--sparkle-end) scale(1); }
        }
      `}</style>

      {/* Top header (classic) */}
      {!compact && <header style={{
        position: 'sticky', top: 0, zIndex: 30,
        background: t.headerBg, backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${t.divider}`,
      }}>
        <div style={{ maxWidth: 920, margin: '0 auto', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8, background: t.accent,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Dumbbell size={15} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: t.text }}>Fitness</div>
              <div style={{ fontSize: 11, color: t.textFaint }}>
                {workoutToday().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {!user?.fitness_only && (
              <Link to="/dashboard" title="Open trading dashboard" style={{
                background: t.surfaceAlt, border: `1px solid ${t.cardBorder}`, borderRadius: 8,
                padding: 8, cursor: 'pointer', color: t.text, display: 'flex',
                alignItems: 'center', justifyContent: 'center', textDecoration: 'none',
              }}>
                <TrendingUp size={16} />
              </Link>
            )}
            <button onClick={() => {
              const next = isDark ? 'light' : 'dark';
              setTheme(next); localStorage.setItem(THEME_KEY, next);
            }} style={{
              background: t.surfaceAlt, border: `1px solid ${t.cardBorder}`, borderRadius: 8,
              padding: 8, cursor: 'pointer', color: t.text, display: 'flex',
            }}>
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ maxWidth: 920, margin: '0 auto', padding: '0 20px', display: 'flex', gap: 4, overflowX: 'auto' }}>
          {TABS.map(x => {
            const active = tab === x.id;
            return (
              <button key={x.id} onClick={() => { setMealsDate(null); setTab(x.id); }} style={{
                padding: '10px 14px', background: 'transparent', border: 'none', cursor: 'pointer',
                color: active ? t.accent : t.textMuted, fontWeight: 600, fontSize: 13,
                display: 'flex', alignItems: 'center', gap: 6, position: 'relative', whiteSpace: 'nowrap',
              }}>
                <x.icon size={14} />
                {x.label}
                {active && <div style={{
                  position: 'absolute', bottom: 0, left: 8, right: 8, height: 2, background: t.accent, borderRadius: 2,
                }} />}
              </button>
            );
          })}
        </div>
      </header>}

      {syncNotice && (
        <div role="status" style={{
          position: 'fixed', bottom: `calc(${rest ? 'max(env(safe-area-inset-bottom), 16px) + 76px' : 'max(env(safe-area-inset-bottom), 18px)'} + var(--fit-bottom, 0px))`, left: '50%',
          transform: 'translateX(-50%)', zIndex: 60, maxWidth: 'calc(100vw - 32px)',
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 12,
          background: t.surface, color: t.text, border: `1px solid ${t.cardBorder}`,
          boxShadow: '0 8px 30px rgba(0,0,0,0.25)', fontSize: 13,
        }}>
          <RotateCcw size={15} color={t.accent} style={{ flexShrink: 0 }} />
          <span>{syncNotice}</span>
          <button onClick={() => setSyncNotice(null)} aria-label="Dismiss" style={{
            background: 'transparent', border: 'none', color: t.textMuted, cursor: 'pointer', padding: 2, display: 'flex',
          }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Main */}
      <main className="fit-page" style={{
        maxWidth: 920, margin: '0 auto',
        padding: compact ? 'max(env(safe-area-inset-top), 12px) 16px calc(96px + env(safe-area-inset-bottom))' : '24px 20px 80px',
      }}>
        {compact && tab !== 'today' && (
          <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em', margin: '6px 4px 16px', color: t.text }}>
            {TABS.find(x => x.id === tab)?.label}
          </h1>
        )}
        {ActiveTab}
      </main>

      {/* Tab bar (compact) */}
      {compact && (
        <nav style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 40,
          paddingBottom: 'env(safe-area-inset-bottom)', background: t.navBg,
          backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderTop: `1px solid ${t.divider}`,
        }}>
          <div style={{ maxWidth: 560, margin: '0 auto', height: 58, display: 'flex' }}>
            {TABS.map(x => {
              const active = tab === x.id;
              return (
                <button key={x.id} onClick={() => { setMealsDate(null); setTab(x.id); window.scrollTo(0, 0); }} style={{
                  flex: 1, background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px 0 0',
                  color: active ? t.accent : t.textFaint, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: 3, fontSize: 10.5, fontWeight: 500, touchAction: 'manipulation',
                }}>
                  <x.icon size={21} strokeWidth={active ? 2.2 : 1.8} />
                  {x.label}
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {rest && (
        <>
          {/* Room to scroll the last card above the pill. */}
          <div style={{ height: 88 }} />
          <RestPill t={t} timer={rest} onChange={setRest}
            savedSec={restPlanEx?.restSec || null}
            autoSec={restPlanEx ? restSecondsFor({ ...restPlanEx, name: rest.exercise || restPlanEx.name, restSec: null }) : null}
            onRemember={restPlanEx ? sec => setState(prev => withPlanRest(prev, restPlanEx.id, sec)) : null}
            rememberFor={restPlanEx?.name || null} />
        </>
      )}
    </div>
  );
}
